import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { fmtTL, CATEGORIES } from "@/lib/api";
import { Zap, Droplet, Wifi, Phone, Building2, Receipt, Plus, CheckCircle2, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ICONS = { elektrik: Zap, su: Droplet, internet: Wifi, telefon: Phone, aidat: Building2, diger: Receipt };

export default function Bills() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "elektrik", branch_id: "", amount: "", due_date: "", invoice_no: "", status: "odenmedi" });

  const { data: bills = [] } = useQuery({ queryKey: ["bills"], queryFn: async () => (await api.get("/bills")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bills"] });
    qc.invalidateQueries({ queryKey: ["expenses"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const createM = useMutation({
    mutationFn: (b) => api.post("/bills", b),
    onSuccess: () => { invalidate(); setOpen(false); toast.success("Fatura eklendi"); setForm({ title: "", category: "elektrik", branch_id: "", amount: "", due_date: "", invoice_no: "", status: "odenmedi" }); },
  });
  const payM = useMutation({
    mutationFn: (id) => api.post(`/bills/${id}/pay`),
    onSuccess: () => {
      invalidate();
      toast.success("Fatura ödendi ve Masraflar'a anında eklendi");
    },
  });
  const delM = useMutation({ mutationFn: (id) => api.delete(`/bills/${id}`), onSuccess: () => { invalidate(); toast.success("Fatura silindi"); } });

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const filtered = filter === "all" ? bills : filter === "unpaid" ? bills.filter((b) => b.status === "odenmedi") : bills.filter((b) => b.category === filter);

  const submit = (e) => {
    e.preventDefault();
    createM.mutate({ ...form, amount: parseFloat(form.amount), recurring: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Faturalar & Giderler</h1>
          <p className="text-slate-400 text-sm mt-1">Elektrik, su, internet, telefon ve aidat takibi</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-bill-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
              <Plus className="w-4 h-4" /> Fatura Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1220] border-slate-800 text-white">
            <DialogHeader><DialogTitle className="font-heading">Yeni Fatura</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Başlık</label>
                <input data-testid="bill-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. Elektrik Faturası" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Fatura No</label>
                <input data-testid="bill-invoice-no-input" required value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. 2026-ELK-00451" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Kategori</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="bill-category-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                      {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Şube</label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="bill-branch-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Tutar (₺)</label>
                  <input data-testid="bill-amount-input" required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Son Ödeme</label>
                  <input data-testid="bill-due-date-input" required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 [color-scheme:dark]" />
                </div>
              </div>
              <DialogFooter>
                <button data-testid="bill-submit-button" type="submit" disabled={createM.isPending} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-lg text-sm">Kaydet</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500 text-xs flex items-center gap-1 mr-1"><Filter className="w-3.5 h-3.5" /> Filtre:</span>
        {[{ k: "all", l: "Tümü" }, { k: "unpaid", l: "Ödenmemiş" }, ...Object.entries(CATEGORIES).map(([k, v]) => ({ k, l: v.label }))].map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.k ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-white"}`}>{f.l}</button>
        ))}
      </div>

      {/* Bills grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((b) => {
          const cat = CATEGORIES[b.category] || CATEGORIES.diger;
          const Icon = ICONS[b.category] || Receipt;
          const paid = b.status === "odendi";
          return (
            <div key={b.id} data-testid={`bill-card-${b.id}`} className="glass rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${cat.color}20`, color: cat.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${paid ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                  {paid ? "Ödendi" : "Ödenmedi"}
                </span>
              </div>
              <div className="mt-4 text-sm font-semibold text-white">{b.title}</div>
              <div className="text-xs text-slate-400">{cat.label} · {branchName(b.branch_id)}</div>
              {b.invoice_no && <div className="text-xs text-slate-500 font-mono mt-0.5" data-testid={`bill-invoice-no-${b.id}`}>No: {b.invoice_no}</div>}
              <div className="mt-3 text-xl font-heading font-bold text-white tabular">{fmtTL(b.amount)}</div>
              <div className="text-xs text-slate-500 mt-1">Son ödeme: {b.due_date}</div>
              <div className="mt-4 flex gap-2">
                {!paid && (
                  <button data-testid={`pay-bill-${b.id}`} onClick={() => payM.mutate(b.id)} className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600/90 hover:bg-emerald-500 text-white text-xs font-semibold py-2 rounded-lg transition-colors">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Öde
                  </button>
                )}
                <button data-testid={`delete-bill-${b.id}`} onClick={() => delM.mutate(b.id)} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && <div className="text-slate-500 text-sm py-16 text-center">Bu filtreye uygun fatura yok.</div>}
    </div>
  );
}
